import React, { useState, useEffect, useRef } from "react";

import { Formik, Form, Field } from "formik";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import { Stack } from "@mui/material";
import { Bot, MessageSquareText, Save, TimerReset } from "lucide-react";
import { i18n } from "../../translate/i18n";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { CloseXIcon } from "../ChatiaIcons";

const useStyles = makeStyles((theme) => {
  const isDark = theme.mode === "dark" || theme.palette.type === "dark";

  return {
    root: {
      display: "flex",
      flexWrap: "wrap",
    },
    dialogPaper: {
      ...(theme.chatiaDS?.dialogPaper || {}),
      "& .MuiDialogContent-root": {
        flex: 1,
        overflowY: "auto !important",
        overflowX: "hidden",
        background: isDark ? "#121212" : "#ffffff",
        ...theme.scrollbarStyles,
      },
      "& .MuiDialogActions-root": {
        flexShrink: 0,
        padding: "12px 16px",
        background: isDark ? "#121212" : "#ffffff",
      },
      "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? `${theme.palette.primary.main} !important` : undefined,
      },
      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isDark ? `${theme.palette.primary.main} !important` : undefined,
      },
    },
    header: {
      ...(theme.chatiaDS?.header || {}),
      flexShrink: 0,
    },
    headerTitle: {
      ...(theme.chatiaDS?.headerTitle || {}),
    },
    closeButton: {
      backgroundColor: theme.chatiaDS?.closeButton?.backgroundColor || "rgba(255,255,255,0.08)",
      color: theme.chatiaDS?.closeButton?.color || "#ffffff",
      transition: theme.chatiaDS?.closeButton?.transition || "all 0.2s ease",
      "&:hover": {
        backgroundColor: theme.chatiaDS?.closeButtonHover?.backgroundColor || "rgba(255,255,255,0.18)",
        color: "#ffffff",
      },
    },
    content: {
      padding: "18px 20px !important",
      maxHeight: "70vh",
    },
    bodyCard: {
      gap: 12,
      display: "flex",
      flexDirection: "column",
    },
    infoCard: {
      background: theme.mode === "dark" ? "#16191f" : "#ffffff",
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
      borderRadius: 12,
      padding: 16,
      boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      marginBottom: 14,
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
      fontSize: 13,
      fontWeight: 700,
      letterSpacing: 0.1,
    },
    cardIcon: {
      width: 32,
      height: 32,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
      border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
      color: theme.palette.primary.main,
      "& svg": {
        color: theme.palette.primary.main,
        stroke: theme.palette.primary.main,
      },
    },
  };
});

const FlowBuilderAddQuestionModal = ({
  open,
  onSave,
  onUpdate,
  data,
  close,
}) => {
  const classes = useStyles();
  const isMounted = useRef(true);

  const initialState = {
    message: "",
    answerKey: "",
    timeoutSeconds: 0,
  };

  const [message, setMessage] = useState("");
  const [activeModal, setActiveModal] = useState(false);
  const [integration, setIntegration] = useState(initialState);
  const [timeoutSeconds, setTimeoutSeconds] = useState(0);
  const [aiTimeoutSeconds, setAiTimeoutSeconds] = useState(0);
  const [labels, setLabels] = useState({
    title: i18n.t("flowBuilderModals.questionModal.titleAdd"),
    btn: i18n.t("flowBuilderModals.questionModal.buttonAdd"),
  });

  useEffect(() => {
    if (open === "edit") {
      const currentIntegration = data.data.typebotIntegration;
      setLabels({
        title: i18n.t("flowBuilderModals.questionModal.titleEdit"),
        btn: i18n.t("flowBuilderModals.questionModal.buttonSave"),
      });
      setMessage(currentIntegration.message);
      setTimeoutSeconds(currentIntegration.timeoutSeconds || data.data.timeoutSeconds || 0);
      setAiTimeoutSeconds(currentIntegration.aiTimeoutSeconds || data.data.aiTimeoutSeconds || 0);
      setIntegration({
        ...currentIntegration,
      });
      setActiveModal(true);
    } else if (open === "create") {
      setLabels({
        title: i18n.t("flowBuilderModals.questionModal.titleCreate"),
        btn: i18n.t("flowBuilderModals.questionModal.buttonAdd"),
      });
      setMessage("");
      setTimeoutSeconds(0);
      setAiTimeoutSeconds(0);
      setIntegration(initialState);
      setActiveModal(true);
    }

    return () => {
      isMounted.current = false;
    };
  }, [open]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const handleSavePrompt = (values) => {
    if (open === "edit") {
      let oldVariable = localStorage.getItem("variables");
      const oldNameKey = data.data.typebotIntegration.answerKey;

      if (oldVariable) {
        oldVariable = JSON.parse(oldVariable);
      } else {
        oldVariable = [];
      }

      oldVariable = oldVariable.filter(item => item !== oldNameKey);
      localStorage.setItem("variables", JSON.stringify([...oldVariable, values.answerKey]));

      handleClose();
      onUpdate({
        ...data,
        data: {
          typebotIntegration: {
            ...values,
            message,
            timeoutSeconds: parseInt(timeoutSeconds) || 0,
            aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0,
          },
          timeoutSeconds: parseInt(timeoutSeconds) || 0,
          aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0,
        },
      });
    } else if (open === "create") {
      let oldVariable = localStorage.getItem("variables");

      if (oldVariable) {
        oldVariable = JSON.parse(oldVariable);
      } else {
        oldVariable = [];
      }

      oldVariable = oldVariable.filter(item => item !== values.answerKey);
      localStorage.setItem("variables", JSON.stringify([...oldVariable, values.answerKey]));

      handleClose();
      onSave({
        typebotIntegration: {
          ...values,
          message,
          timeoutSeconds: parseInt(timeoutSeconds) || 0,
          aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0,
        },
        timeoutSeconds: parseInt(timeoutSeconds) || 0,
        aiTimeoutSeconds: parseInt(aiTimeoutSeconds) || 0,
      });
    }
  };

  const CardTitle = ({ icon: Icon, children }) => (
    <div className={classes.cardHeader}>
      <span className={classes.cardIcon}>
        <Icon size={16} />
      </span>
      <span>{children}</span>
    </div>
  );

  return (
    <div className={classes.root}>
      <Dialog
        open={activeModal}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{ className: classes.dialogPaper }}
      >
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>{labels.title}</Typography>
          <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
            <CloseXIcon style={{ fontSize: 18 }} />
          </IconButton>
        </div>
        <Formik
          initialValues={integration}
          enableReinitialize={true}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSavePrompt(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form style={{ width: "100%" }}>
              <DialogContent className={classes.content}>
                <Stack className={classes.bodyCard}>
                  <Stack className={classes.infoCard}>
                    <CardTitle icon={MessageSquareText}>
                      {i18n.t("flowBuilderModals.questionModal.fields.message")}
                    </CardTitle>
                    <TextField
                      label={i18n.t("flowBuilderModals.questionModal.fields.message")}
                      multiline
                      minRows={7}
                      name="message"
                      error={touched.message && Boolean(errors.message)}
                      helperText={touched.message && errors.message}
                      variant="outlined"
                      margin="dense"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      fullWidth
                      required
                    />
                  </Stack>

                  <Stack className={classes.infoCard}>
                    <CardTitle icon={Save}>
                      {i18n.t("flowBuilderModals.questionModal.fields.saveAnswer")}
                    </CardTitle>
                    <Field
                      as={TextField}
                      label={i18n.t("flowBuilderModals.questionModal.fields.saveAnswer")}
                      name="answerKey"
                      error={touched.answerKey && Boolean(errors.answerKey)}
                      helperText={touched.answerKey && errors.answerKey}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      required
                    />
                  </Stack>

                  <Stack className={classes.infoCard}>
                    <CardTitle icon={TimerReset}>
                      {i18n.t("flowBuilderModals.questionModal.timeout.title")}
                    </CardTitle>
                    <TextField
                      label={i18n.t("flowBuilderModals.questionModal.timeout.label")}
                      type="number"
                      variant="outlined"
                      margin="dense"
                      value={timeoutSeconds}
                      onChange={e => setTimeoutSeconds(e.target.value)}
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      helperText={timeoutSeconds > 0
                        ? i18n.t("flowBuilderModals.questionModal.timeout.helperText", { seconds: timeoutSeconds })
                        : ""}
                    />
                  </Stack>

                  <Stack className={classes.infoCard}>
                    <CardTitle icon={Bot}>
                      {i18n.t("flowBuilderModals.questionModal.aiTimeout.title")}
                    </CardTitle>
                    <TextField
                      label={i18n.t("flowBuilderModals.questionModal.aiTimeout.label")}
                      type="number"
                      variant="outlined"
                      margin="dense"
                      value={aiTimeoutSeconds}
                      onChange={e => setAiTimeoutSeconds(e.target.value)}
                      fullWidth
                      InputProps={{ inputProps: { min: 0 } }}
                      helperText={aiTimeoutSeconds > 0
                        ? i18n.t("flowBuilderModals.questionModal.aiTimeout.helperText", { seconds: aiTimeoutSeconds })
                        : ""}
                    />
                  </Stack>
                </Stack>
              </DialogContent>
              <ChatiaDialogActions
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
                saveText={labels.btn}
              />
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default FlowBuilderAddQuestionModal;
